import React from "react";
import * as Comp from "../../../../components";
import * as Forms from "../../../../forms";
import * as M from "../../../../models";
import { Api } from "../../../../services/Api";
import Settings from "../../../../settings";
import { Utility } from "../../../../utils";
import { NavigationTypes } from "../../../../domain";

interface OwnProps {
    api: Api;
    onCancel?: () => void;
    onError?: (error: string) => void;
    onProcessing?: () => void;
    onUpdated?: (navItem: M.AdminNav.NavListItem) => void;
    navItemsGetter: () => M.AdminNav.NavListItem[];
}

interface OwnState {
    formModel: Forms.AdminNav.NavRedirectEdit;
    navItem?: M.AdminNav.NavListItem;
    formValuesSet: boolean;
    show: boolean;
}

export class EditNavRedirectModal extends Comp.Validation.ComponentWithValidatedForm<OwnProps, OwnState> {

    public constructor(props: any) {
        super(props);

        const formModel = new Forms.AdminNav.NavRedirectEdit();
        this.state = {
            ...this.getInitialState(formModel),
            formValuesSet: false,
            show: false,
        };

        this.onSubmit = this.onSubmit.bind(this);
    }

    public shouldComponentUpdate(nextProps: Readonly<OwnProps>, nextState: Readonly<OwnState>, nextContext: any): boolean {
        // const currItems = this.props.navItemsGetter();
        const nextItems = nextProps.navItemsGetter();

        if (this.state.navItem && !this.state.formValuesSet && nextItems.length) {
            // nav items received
            // we need to set the parent select field value
            const formModel = { ...this.state.formModel };
            const parentIds = this.state.navItem.parentIds;
            const parents = nextItems.filter((n) => this.state.navItem
                && parentIds.length
                && parentIds[parentIds.length - 1] === n.id);

            if (parents.length) {
                const parent = parents[0];
                formModel.data.parentId = Utility.toReactSelectOption(parent, parent.title, parent.id);
            }

            // we need to set the redirect-to field value if it is an internal redirect
            if (this.state.navItem.url && this.state.navItem.url.indexOf("/") === 0) {
                const redirectTos = nextItems.filter((n) => this.state.navItem
                    && n.url === this.state.navItem.url
                    && NavigationTypes.isContentType(n.typeId));

                if (redirectTos.length) {
                    const redirectTo = redirectTos[0];
                    formModel.data.redirectToId = Utility.toReactSelectOption(redirectTo, redirectTo.title, redirectTo.id);
                    formModel.data.isExternal = false;
                }
            }
            else
                formModel.data.isExternal = true;

            this.setState({ ...this.state, formModel, formValuesSet: true });
            return false;
        }

        return true;
    }

    public close() {
        this.setState({
            ...this.state,
            ...this.getInitialState(this.navItemToFormModel()),
            formValuesSet: false,
            navItem: undefined,
            show: false });
    }

    public show(navItem?: M.AdminNav.NavListItem) {
        const formModel = this.navItemToFormModel(navItem);

        this.setState({
            ...this.state,
            ...this.getInitialState(formModel),
            navItem,
            show: true,
        });
    }

    public render() {
        const model = this.state.formModel.data as Forms.AdminNav.NavRedirectEditData;
        const isNew = model.id === 0;

        // setup url editor.
        // default if new item
        let urlOrParentEditor = null;

        if (model.isExternal) {
            urlOrParentEditor = <Comp.Editors.TextField
                formComponent={this}
                name="url"
                css="txt"
                placeholder="http[s]://... for external URLs, or #... for internal jump-to links"
                required
            />;
        }
        else {
            urlOrParentEditor = <Comp.Editors.SelectField
                formComponent={this}
                isAsync
                name="redirectToId"
                required
                select={{
                    cache: false,
                    loadOptions: (search: string) => this.searchNavItems(search),
                    optionRenderer: (n: M.AdminNav.NavListItem) => <span>{n.title}<br /><span className="mute">{n.fullTitle}</span></span>,
                }}
            />;
        }

        return <Comp.Modal.ModalForm
            bsSize="lg"
            formComponent={this}
            onCancel={() => this.handleCancel()}
            processing={this.state.formProcessing}
            show={this.state.show}
            title={(isNew ? "Add new" : "Edit") + " navigation redirect"}
        >
            <Comp.Validation.ValidatedForm
                formComponent={this}
                model={this.state.formModel.data}
                onValidSubmit={this.onSubmit}
                schema={this.state.formModel.rules}
                ref={(ref: any) => this.form = ref}
            >
                <Comp.Editors.CheckboxField formComponent={this} name="active" />
                <Comp.Editors.CheckboxField formComponent={this} name="onMenu" />
                <Comp.Editors.CheckboxField formComponent={this} name="isExternal" hint="Redirects to an external website or is a jump-to (#) link" />
                <Comp.Editors.TextField formComponent={this} name="title" css="txt" />

                <Comp.Editors.SelectField
                    formComponent={this}
                    isAsync
                    name="parentId"
                    select={{
                        cache: false,
                        loadOptions: (search: string) => this.searchNavItems(search),
                        optionRenderer: (n: M.AdminNav.NavListItem) => <span>{n.title}<br /><span className="mute">{n.fullTitle}</span></span>,
                    }}
                />

                {urlOrParentEditor}

            </Comp.Validation.ValidatedForm>
        </Comp.Modal.ModalForm>;
    }

    /**
     * If provided, converts the nav item into a form model. Otherwise returns a new form model.
     * @param [navItem] The nav item
     * @returns M.AdminNav.NavListItem
     */
    private navItemToFormModel(navItem?: M.AdminNav.NavListItem) {
        const formModel = new Forms.AdminNav.NavRedirectEdit();
        if (!navItem)
            return formModel;
        else {
            formModel.data.active = navItem.statusId === 1;
            formModel.data.id = navItem.id;
            formModel.data.onMenu = navItem.onNav;
            formModel.data.title = navItem.title;
            formModel.data.url = navItem.url;
            return formModel;
        }
    }

    private handleCancel() {
        if (this.props.onCancel)
            this.props.onCancel();

        this.close();
    }

    private async onSubmit(model: Forms.AdminNav.NavRedirectEditData) {
        this.toggleProcessing(true);

        const updateResult = await this.props.api.put<M.AdminNav.NavListItem>(Api.endpoints.admin.nav.redirect, {
            body: JSON.stringify({
                ...model,
                appId: Settings.AppID,
                parentId: model.parentId === null ? null : (model.parentId as M.AdminNav.NavListItem).id,
                redirectToId: model.redirectToId === null ? null : (model.redirectToId as M.AdminNav.NavigatableRecordItem).id,
            }),
        });

        if (updateResult.isSuccess && updateResult.data) {
            if (this.props.onUpdated)
                this.props.onUpdated(updateResult.data);
            this.close();
        }
        else {
            if (this.props.onError)
                this.props.onError(updateResult.errors.join("; "));

            this.setFormErrors(updateResult.errors.length ? updateResult.errors : [updateResult.message || "Server error"]);
        }

        this.toggleProcessing(false);
    }

    private searchNavItems(search: string) {
        if (!search || search.length < 2)
            return Promise.resolve({ options: [] });

        const navItems = this.props.navItemsGetter()
            .filter((n) => {
                return n.title.toLowerCase().indexOf(search.toLowerCase()) > -1
                    && [n.id].concat(n.parentIds).indexOf(this.state.formModel.data.id) === -1; // must exclude nav items who are children of the item being edited.
            })
            .map((n) => (Utility.toReactSelectOption(n, n.title, n.id)));

        const scoredItems = Utility.sortFilteredArrayByRelevance(navItems, (n) => n.title, search, ["title"]);

        return new Promise((resolve, reject) => {
            resolve({
                options: scoredItems,
            });
        });
    }
}



// WEBPACK FOOTER //
// ./src/containers/adminNav/list/components/EditNavRedirectModal.tsx